import { useCallback, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';
import { Box, Paper, Typography } from '@/shared/ui';

import { FWDCURVE_CATALOG_PATH, SPOT_CATALOG_PATH } from '@/config/constants';
import useDocumentTitleRename from '@/shared/hooks/useDocumentTitleRename';
import getAmountLabel from '@/shared/lib/format/getAmountLabel';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';
import Tabs from '@/shared/ui/primitives/Tabs';

import { FactsheetDialogButton } from '@/features/factsheet';

import CatalogSkeleton from '../components/CatalogSkeleton/CatalogSkeleton';
import CatalogTable from '../components/CatalogTable/CatalogTable';
import useCatalog from '../hooks/useCatalog';
import { FilterProvider } from '../state/filter.context';
import { useFilter } from '../state/useFilter';

const catalogTabs = [
  {
    name: 'Spot',
    label: 'Spot'
  },
  {
    name: 'Forward curves',
    label: 'Forward Curves'
  }
];

const pageInformation = {
  mainPageTitle: 'CatalogTable'
};

const CatalogBase = () => {
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const isForwardCurves =
    pathname.includes(FWDCURVE_CATALOG_PATH) || pathname.includes('forward-curves');

  const {
    status,
    filterOpen,
    setFilterOpen,
    table,
    activeTab,
    expandedData,
    setExpandedData,
    showDiscontinued,
    setShowDiscontinued
  } = useCatalog(isForwardCurves);

  const catalogLabel = useMemo(() => {
    if (isForwardCurves) return 'curve';
    return expandedData ? 'item' : 'code';
  }, [expandedData, isForwardCurves]);

  useDocumentTitleRename('GX Go | Catalog');
  const { clearAllFilters } = useFilter();
  const navigate = useNavigate();

  const onTabChange = useCallback(() => {
    clearAllFilters();
    navigate(`/catalog/${isForwardCurves ? SPOT_CATALOG_PATH : FWDCURVE_CATALOG_PATH}`);
  }, [clearAllFilters, isForwardCurves, navigate]);

  if (status === 'loading') {
    return <CatalogSkeleton />;
  }

  return (
    <>
      <Box sx={{ display: 'flex', width: '100%', my: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-end', width: 390 }}>
          <Typography
            noWrap
            sx={{ mr: 3 }}
            variant='h2'
          >
            Catalog
          </Typography>
          <Typography
            noWrap
            sx={{
              fontSize: '20px'
            }}
          >
            {getAmountLabel(table.getFilteredRowModel().rows.length, catalogLabel)}
          </Typography>
        </Box>
        <PageInformationContext value={pageInformation}>
          <Tabs
            activeTab={activeTab}
            minTabWidth={300}
            tabs={catalogTabs}
            onChange={onTabChange}
          />
        </PageInformationContext>
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <CatalogTable
          expandedData={expandedData}
          filterOpen={filterOpen}
          isForwardCurves={isForwardCurves}
          renderFactsheetButton={(code, mode) => (
            <FactsheetDialogButton
              code={code}
              mode={mode}
            />
          )}
          setExpandedData={setExpandedData}
          setFilterOpen={setFilterOpen}
          setShowDiscontinued={setShowDiscontinued}
          showDiscontinued={showDiscontinued}
          table={table}
        />
      </Paper>
    </>
  );
};

export const CatalogPage = () => (
  <FilterProvider>
    <CatalogBase />
  </FilterProvider>
);
